import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaFilter, FaEye, FaAngleLeft, FaAngleRight, FaSpinner, FaExclamationTriangle, FaUsers, FaUser, FaFileAlt, FaFileContract, FaTimes } from 'react-icons/fa';
// Import rmApi instead of dsoApi for fetching the list
import { rmApi } from '../../services/api'; // Changed import

function RMOwners() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All'); // This state holds the selected filter value
  const [ownersList, setOwnersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch DSO list from API using rmApi client
  const fetchOwnersList = async () => {
    setLoading(true);
    setError('');
    try {
      // Build parameters for the API call
      const params = {
        skip: (currentPage - 1) * entriesPerPage,
        limit: entriesPerPage,
        search: debouncedSearchTerm || undefined, // Pass search term if not empty
        // Pass status filter if not 'All', ensure name matches backend/api.js
        status_filter: statusFilter !== 'All' ? statusFilter : undefined
      };

      // Use the rmApi client to fetch the data
      // Call the new method getAssignedDsos from rmApi
      const response = await rmApi.getAssignedDsos(params); // Changed API call

      setOwnersList(response.data.items);
      setTotalItems(response.data.total);
      setTotalPages(Math.ceil(response.data.total / entriesPerPage));

    } catch (err) {
      console.error("Error fetching assigned DSO list:", err); // Updated log message
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
         setError('Authentication error. Please log in again.');
      } else {
         setError('Failed to load assigned DSOs. Please try again later.'); // Updated error message
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Fetch data when pagination, search, or filters change
  useEffect(() => {
    fetchOwnersList();
  }, [currentPage, entriesPerPage, debouncedSearchTerm, statusFilter]);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, entriesPerPage]);

  // Function to navigate to details page
  const viewDetails = (id) => {
    navigate(`/rm/owners/${id}/detail`);
  };
  
  // Generate page numbers for pagination
  const getVisiblePageNumbers = () => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    
    return [currentPage - 1, currentPage, currentPage + 1];
  };

  return (
    <div className="rm-home-dashboard">
      <div className="rm-dashboard-welcome">
        <h1>DSO Partners</h1>
        <p>View and manage your assigned DSO partners</p>
      </div>
      
      <div className="rm-dashboard-filters">
        <div className="rm-search-filter">
          <div className="rm-search-input">
            <FaSearch className="rm-search-icon" />
            <input
              type="text"
              placeholder="Search by name or email" // Updated placeholder
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="rm-status-filter">
          <FaFilter className="rm-filter-icon" />
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Pending">Pending</option>
            <option value="Verified">Verified</option>
            <option value="Deactive">Inactive</option>
          </select>
        </div>
      </div>
      
      <div className="rm-dashboard-card rm-dashboard-transactions">
        <div className="rm-card-header">
          <h3>DSO List</h3>
          <div className="rm-entries-control">
            <span>Show </span>
            <select
              value={entriesPerPage}
              onChange={(e) => setEntriesPerPage(Number(e.target.value))}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span> entries</span>
          </div>
        </div>
        <div className="rm-transaction-list">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Agents</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="rm-loading-state">
                    <FaSpinner className="rm-spinner" />
                    <span>Loading owners list...</span>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="7" className="rm-error-state">
                    <FaExclamationTriangle className="rm-error-icon" />
                    <span>{error}</span>
                  </td>
                </tr>
              ) : ownersList.length > 0 ? (
                ownersList.map((owner) => (
                  <tr key={owner.id}>
                    <td className="rm-owner-name">{owner.name}</td>
                    <td>{owner.email}</td>
                    <td>{owner.phone}</td>
                    <td>{owner.location || 'N/A'}</td> {/* Added fallback */}
                    <td className="rm-agent-count">
                      <FaUsers className="rm-agent-icon" />
                      {/* Use agents_count from the new API response */}
                      <span>{owner.agents !== undefined ? owner.agents : 0}</span>
                    </td>
                    {/* Update Status Cell Rendering */}
                    <td>
                      {owner.status === 'Verified' ? (
                        <span className="rm-status-badge rm-status-verified">{owner.status}</span>
                      ) : owner.status === 'Active' ? (
                        <span className="rm-status-badge rm-status-active">{owner.status}</span>
                      ) : owner.status === 'Pending' ? (
                        <span className="rm-status-badge rm-status-pending">{owner.status}</span>
                      ) : owner.status === 'Inactive' ? (
                        <span className="rm-status-badge rm-status-inactive">{owner.status}</span>
                      ) : (
                        owner.status || 'N/A' // Fallback
                      )}
                    </td>
                    <td>
                      <button
                        className="rm-view-details-btn"
                        onClick={() => viewDetails(owner.id)}
                      >
                        <FaEye /> View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="rm-no-data">
                    No DSO partners found matching the search criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="rm-pagination-container">
          <div className="rm-pagination-info">
            Showing {ownersList.length > 0 ? ((currentPage - 1) * entriesPerPage) + 1 : 0} to {((currentPage - 1) * entriesPerPage) + ownersList.length} of {totalItems} entries
          </div>
          {/* Pagination Controls */}
          <div className="rm-pagination">
            <button 
              className="rm-pagination-btn" 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <FaAngleLeft />
            </button>
            
            {getVisiblePageNumbers().map(number => (
              <button
                key={number}
                className={`rm-pagination-btn ${number === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(number)}
              >
                {number}
              </button>
            ))}
            
            <button
              className="rm-pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <FaAngleRight />
            </button>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .rm-home-dashboard {
          color: #1e293b;
        }
        
        .rm-dashboard-welcome {
          margin-bottom: 24px;
        }
        
        .rm-dashboard-welcome h1 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0 0 8px;
          color: #1e293b;
        }
        
        .rm-dashboard-welcome p {
          margin: 0;
          color: #64748b;
        }
        
        .rm-dashboard-filters {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        
        .rm-search-filter {
          flex: 1;
          min-width: 200px;
        }
        
        .rm-search-input {
          position: relative;
        }
        
        .rm-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
          font-size: 14px;
        }
        
        .rm-search-input input {
          width: 100%;
          padding: 10px 12px 10px 36px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
          transition: all 0.2s;
        }
        
        .rm-search-input input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
        }
        
        .rm-status-filter {
          position: relative;
          width: 200px;
        }
        
        .rm-filter-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #64748b;
          font-size: 14px;
          pointer-events: none;
        }
        
        .rm-status-filter select {
          width: 100%;
          padding: 10px 12px 10px 36px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 14px;
          appearance: none;
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E") no-repeat;
          background-position: right 10px center;
          background-color: white;
          cursor: pointer;
          outline: none;
          transition: all 0.2s;
        }
        
        .rm-status-filter select:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
        }
        
        .rm-dashboard-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
          margin-bottom: 24px;
        }
        
        .rm-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .rm-card-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }
        
        .rm-entries-control {
          font-size: 0.875rem;
          color: #64748b;
          display: flex;
          align-items: center;
        }
        
        .rm-entries-control select {
          margin: 0 8px;
          padding: 4px 8px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          font-size: 0.875rem;
          outline: none;
        }
        
        .rm-transaction-list {
          padding: 0 20px 16px;
        }
        
        .rm-transaction-list table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .rm-transaction-list th {
          text-align: left;
          padding: 12px 8px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .rm-transaction-list td {
          padding: 12px 8px;
          font-size: 0.875rem;
          border-bottom: 1px solid #f1f5f9;
          /* Add vertical alignment */
          vertical-align: middle;
        }

        .rm-transaction-list tr:last-child td {
          border-bottom: none;
        }
        
        .rm-owner-name {
          font-weight: 500;
        }
        
        .rm-agent-count {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .rm-agent-icon {
          color: #64748b;
          font-size: 14px;
        }
        
        .rm-view-details-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #6366f1;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 6px 12px;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .rm-view-details-btn:hover {
          background: #4f46e5;
        }
        
        .rm-pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-top: 1px solid #f1f5f9;
          flex-wrap: wrap;
          gap: 12px;
        }
        
        .rm-pagination-info {
          font-size: 0.75rem;
          color: #64748b;
        }
        
        .rm-pagination {
          display: flex;
          gap: 4px;
        }
        
        .rm-pagination-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: white;
          color: #64748b;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .rm-pagination-btn:hover:not(:disabled) {
          background: #f1f5f9;
          color: #1e293b;
        }
        
        .rm-pagination-btn.active {
          background: #6366f1;
          color: white;
          border-color: #6366f1;
        }
        
        .rm-pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .rm-loading-state, .rm-error-state, .rm-no-data {
          text-align: center;
          padding: 30px 0;
        }
        
        .rm-loading-state, .rm-error-state {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .rm-spinner {
          animation: spin 1s linear infinite;
        }
        
        .rm-error-icon {
          color: #ef4444;
        }
        
        .rm-error-state {
          color: #ef4444;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Responsive adjustments */
        @media screen and (max-width: 992px) {
          .rm-card-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          
          .rm-pagination-container {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .rm-dashboard-filters {
            flex-direction: column;
          }
          
          .rm-status-filter {
            width: 100%;
          }
        }

        /* Add Badge Styles */
        .rm-status-badge {
          display: inline-block;
          padding: 0.35em 0.65em; /* Adjust padding like Bootstrap */
          font-size: 0.75em; /* Smaller font size */
          font-weight: 700;
          line-height: 1;
          color: #fff;
          text-align: center;
          white-space: nowrap;
          vertical-align: baseline;
          border-radius: 0.375rem; /* Rounded corners */
        }

        .rm-status-verified {
          background-color: #10b981; /* Green color for Verified (like success) */
        }
      `}</style>
    </div>
  );
}

export default RMOwners;
